import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Whether `el` is entirely hidden behind the clipped edge of some scrollable ancestor (or the
 * viewport) - i.e. scrolled out of its own visible container, not just out of the window.
 */
function isClippedOut(el) {
  const rect = el.getBoundingClientRect();
  // jsdom (and other no-layout test environments) always reports an all-zero rect - treat that
  // as "layout unavailable" rather than "scrolled out of view".
  if (rect.width === 0 && rect.height === 0 && rect.top === 0 && rect.left === 0) return false;
  if (rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) {
    return true;
  }
  for (let ancestor = el.parentElement; ancestor; ancestor = ancestor.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(ancestor);
    if (!/auto|scroll|hidden/.test(overflowX) && !/auto|scroll|hidden/.test(overflowY)) continue;
    const ancestorRect = ancestor.getBoundingClientRect();
    if (
      rect.bottom <= ancestorRect.top ||
      rect.top >= ancestorRect.bottom ||
      rect.right <= ancestorRect.left ||
      rect.left >= ancestorRect.right
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Drop-in replacement for react-select's built-in MenuPortal (pass as
 * `components={{ MenuPortal: RepositioningMenuPortal }}` alongside `menuPortalTarget`).
 *
 * react-select only recomputes the portaled menu's position (via the control's
 * getBoundingClientRect()) when it re-renders. That's fine for window scroll, but a select
 * living inside a modal/accordion with its own internally-scrolled body never re-renders on
 * that container's scroll, so the menu is left behind - visually detached from its control.
 *
 * react-select only mounts this component while the menu is open, so re-measuring on every
 * scroll/resize while mounted is exactly "re-measure while the menu is open" - no need to track
 * open/close state separately.
 *
 * Scrolling the control itself out of its container's visible area (e.g. above a modal's own
 * header) doesn't make it disappear - getBoundingClientRect() still reports where it "would be",
 * so without this check the menu would keep floating there, detached from anything visible.
 */
function RepositioningMenuPortal(props) {
  const { appendTo, children, className, controlElement, cx, innerProps, getStyles } = props;
  const [, forceRemeasure] = useState(0);

  useEffect(() => {
    const handleReflow = (event) => {
      // Scrolling the menu's own option list doesn't move the control - skip the wasted render.
      if (event.target instanceof Element && event.target.closest('.menu-portal')) return;
      forceRemeasure((tick) => tick + 1);
    };
    document.addEventListener('scroll', handleReflow, true);
    window.addEventListener('resize', handleReflow);
    return () => {
      document.removeEventListener('scroll', handleReflow, true);
      window.removeEventListener('resize', handleReflow);
    };
  }, []);

  if (!appendTo || !controlElement) return null;
  if (isClippedOut(controlElement)) return null;

  const rect = controlElement.getBoundingClientRect();
  const style = {
    ...getStyles('menuPortal', { offset: rect.bottom, position: 'fixed', rect }),
    position: 'fixed',
    top: 0,
    left: 0,
    width: rect.width,
    transform: `translate(${Math.round(rect.left)}px, ${Math.round(rect.bottom)}px)`,
  };

  return createPortal(
    <div className={cx({ 'menu-portal': true }, className)} style={style} {...innerProps}>
      {children}
    </div>,
    appendTo,
  );
}

export default RepositioningMenuPortal;

/**
 * react-select only switches to a portaled menu when `menuPortalTarget` (or `menuPosition:
 * 'fixed'`) is set - that decision happens inside react-select itself, before it looks at
 * `components`, so a portal target still has to be passed alongside the component override.
 * This bundles both into one spreadable prop bag: `<Select {...MENU_PORTAL_PROPS} .../>`.
 */
export const MENU_PORTAL_PROPS = {
  menuPortalTarget: document.body,
  components: { MenuPortal: RepositioningMenuPortal },
};
